import { type FC } from "react";

import { Card, Group, Progress, SimpleGrid, Stack, Text } from "@mantine/core";
import { useIntl } from "react-intl";

import { LensPayoutState } from "../../../gql-generated/types";
import { MonthPayout } from "../../../lens-creator-payout/components/messages.generated";
import { formatMilliUsd } from "../../../lens-creator-payout/utils/formatMilliUsd";
import { type LensScoreWindowFieldsFragment } from "../../api/queries";
import { card } from "../top-performer/TopPerformerTab.module.css";

import styles from "./LensPlusTab.module.css";
import { messages } from "./messages.generated";

export const PayoutCycleCard: FC<{
    month: string;
    window: LensScoreWindowFieldsFragment | null;
    paidAmountMilliUsd: number;
    lastPayout?: { month: string; amountMilliUsd: number } | null;
}> = ({ month, window, paidAmountMilliUsd, lastPayout }) => {
    const intl = useIntl();
    const start = new Date(`${month}-01T00:00:00Z`);
    const end = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 0));
    const now = new Date();
    const current = month === now.toISOString().slice(0, 7);
    const date = (value: string | Date) => intl.formatDate(value, { month: "short", day: "numeric", timeZone: "UTC" });
    const monthLabel = intl.formatDate(start, { month: "long", year: "numeric", timeZone: "UTC" });
    const paid = window?.payout.state === LensPayoutState.Paid;
    let amount: number | undefined;
    if (current) amount = lastPayout?.amountMilliUsd;
    else if (paid || paidAmountMilliUsd > 0) amount = paidAmountMilliUsd;
    let status = current ? "awaiting" : undefined;
    if (paid) status = "paid";
    else if (window?.payout.state === LensPayoutState.Pending || window?.payout.state === LensPayoutState.NotDue)
        status = "pending";
    else if (window) status = "noPayout";
    return (
        <Card className={card}>
            <Group justify="space-between" align="flex-start">
                <Stack gap="xs">
                    <h3 className={`heading-h400 ${styles.heading}`}>
                        {intl.formatMessage(messages.cycleTitle, { month: monthLabel })}
                    </h3>
                    <Text className="paragraph-p200" c="dimmed">
                        {intl.formatMessage(messages.cycleSubtitle, {
                            current: String(current),
                            day: now.getUTCDate(),
                            days: end.getUTCDate(),
                        })}
                    </Text>
                </Stack>
                <Stack gap={0} className={styles.amount}>
                    <Text className="paragraph-p200" c="dimmed">
                        {current ? (
                            intl.formatMessage(messages.lastPayout)
                        ) : (
                            <MonthPayout month={intl.formatDate(start, { month: "long", timeZone: "UTC" })} />
                        )}
                    </Text>
                    <Text className="heading-h500">{amount === undefined ? "-" : formatMilliUsd(amount)}</Text>
                    {current && lastPayout && (
                        <Text className="paragraph-p200" c="dimmed">
                            {intl.formatMessage(messages.lastPayoutMonth, {
                                month: intl.formatDate(new Date(`${lastPayout.month}-01T00:00:00Z`), {
                                    month: "long",
                                    year: "numeric",
                                    timeZone: "UTC",
                                }),
                            })}
                        </Text>
                    )}
                </Stack>
            </Group>
            <Progress
                value={current ? (now.getUTCDate() / end.getUTCDate()) * 100 : 100}
                color={paid && !current ? "var(--colors-green-v200)" : "var(--colors-yellow-v200)"}
                radius="xl"
                my="lg"
                aria-label={intl.formatMessage(messages.cycleTitle, { month: monthLabel })}
            />
            <SimpleGrid cols={{ base: 1, sm: 3 }}>
                <Stack gap={0}>
                    <Text className="subheading-sh200">
                        {intl.formatMessage(messages.cycleStarted, { date: date(start) })}
                    </Text>
                    <Text className="paragraph-p200" c="dimmed">
                        {intl.formatMessage(messages.cycleReset)}
                    </Text>
                </Stack>
                <Stack gap={0} className={styles.cycleMiddle}>
                    <Text className="subheading-sh200">
                        {current
                            ? intl.formatMessage(messages.cycleToday, { date: date(now) })
                            : status
                              ? intl.formatMessage(messages.statusLabel, { status })
                              : intl.formatMessage(messages.cycleClosed)}
                    </Text>
                    {current && (
                        <Text className="paragraph-p200" c="dimmed">
                            {intl.formatMessage(messages.cycleUpdates)}
                        </Text>
                    )}
                </Stack>
                <Stack gap={0} className={styles.amount}>
                    <Text className="subheading-sh200">
                        {intl.formatMessage(messages.cycleCloses, { date: date(end) })}
                    </Text>
                    <Text className="paragraph-p200" c="dimmed">
                        {paid && window.payout.paidOn
                            ? intl.formatMessage(messages.paidOn, { date: date(window.payout.paidOn) })
                            : intl.formatMessage(messages.cyclePayoutBy)}
                    </Text>
                </Stack>
            </SimpleGrid>
        </Card>
    );
};
